import type { ReactNode } from 'react';

export interface SpinnerProps {
  /** スクリーンリーダーに読み上げさせる説明 / 視覚的にも表示。default 「読込中…」。 */
  label?: ReactNode;
  /** ラベルを視覚的に隠すか (a11y には残る)。default false。 */
  hideLabel?: boolean;
  /** 円形ローダーのサイズ (px)。default 24。 */
  size?: number;
  className?: string;
}

/**
 * 共通スピナー (Sprint AM: UI2 無限スピナー解消、docs/16 §10-2 P1 H4)。
 *
 * a11y:
 *   - `role="status"` + `aria-live="polite"` で SR にロード状態を通知
 *   - label 既定 「読込中…」、必要なら custom (例 「投稿一覧を読込中…」) 可
 *   - SVG は aria-hidden、テキストで識別する設計
 *
 * 視覚:
 *   - tailwind `animate-spin` + `border-t-primary-600` (shared-ui token に揃える)
 *   - inline-flex: 行内 / 中央寄せ どちらでも親側で制御可
 *
 * 呼出例:
 *   <Spinner label="投稿一覧を読込中…" />
 *   <Spinner hideLabel />  // icon-only (button 内など)
 *
 * 30s タイムアウト UX は呼出側で `useLoadingTimeout` と組合せる。Spinner 単体は時間概念を持たない。
 */
export function Spinner({
  label = '読込中…',
  hideLabel = false,
  size = 24,
  className = '',
}: SpinnerProps) {
  return (
    <span
      role="status"
      aria-live="polite"
      className={`inline-flex items-center gap-2 text-sm text-slate-600 ${className}`.trim()}
    >
      <svg
        aria-hidden="true"
        className="animate-spin"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path
          d="M22 12a10 10 0 0 1-10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          className="text-primary-600"
        />
      </svg>
      <span className={hideLabel ? 'sr-only' : ''}>{label}</span>
    </span>
  );
}
